import { Injectable, PLATFORM_ID, inject, isDevMode } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

const STORAGE_KEY = 'cnt-analytics';
const ANALYTICS_IDB_KEY = 'analytics:events';
const BUFFER_MAX = 500;
/** track() is the highest write-frequency call site in the app (every
 *  tracked event) — batch writes behind a short timer instead of one IDB
 *  transaction per event, since IDB's per-transaction overhead makes that
 *  worth avoiding here specifically (unlike the cheap synchronous
 *  localStorage.setItem this replaces). */
const FLUSH_DELAY_MS = 300;

/**
 * Funnel moments that are also standard Meta Pixel events, so Facebook/Instagram
 * ads can be judged on what they lead to rather than page views.
 */
const META_PIXEL_EVENTS: Record<string, string> = {
  search: 'Search',
  view_item: 'ViewContent',
  add_to_wishlist: 'AddToWishlist',
  begin_checkout: 'InitiateCheckout',
  booking_request: 'Lead',
  purchase: 'Purchase',
  sign_up: 'CompleteRegistration',
};

/** GA4 limits: 25 parameters per event, 40-character names, 100-character values. */
const GA4_MAX_PARAMS = 25;
const GA4_MAX_NAME = 40;
const GA4_MAX_VALUE = 100;

/**
 * Props as GA4 event parameters: only strings, numbers and booleans (booleans
 * as "true"/"false"), within GA4's limits. Anything else (objects, arrays,
 * undefined) is left out rather than sent as "[object Object]".
 */
export function toGa4Params(props: Record<string, unknown>): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(props)) {
    if (Object.keys(out).length >= GA4_MAX_PARAMS) break;
    const name = key.replace(/[^A-Za-z0-9_]/g, '_').slice(0, GA4_MAX_NAME);
    if (typeof value === 'number' && Number.isFinite(value)) out[name] = value;
    else if (typeof value === 'string') out[name] = value.slice(0, GA4_MAX_VALUE);
    else if (typeof value === 'boolean') out[name] = String(value);
  }
  return out;
}

interface ITrackingWindow {
  gtag?: (...args: unknown[]) => void;
  fbq?: (...args: unknown[]) => void;
}

export interface IAnalyticsEvent {
  event: string;
  ts: number;
  [prop: string]: unknown;
}

/**
 * Analytics events. Each `track()` goes to GA4 (and, for the funnel moments
 * in META_PIXEL_EVENTS, the Meta Pixel) once the tracking scripts are on the
 * page — app.component loads them only in production and only after the
 * visitor accepts cookies, so before consent nothing leaves the browser.
 * Events are also buffered locally (IndexedDB) and console-logged in dev.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private platformId = inject<object>(PLATFORM_ID);

  private readonly store = createIdbKvStore<IAnalyticsEvent[]>({
    legacyKeys: { [ANALYTICS_IDB_KEY]: STORAGE_KEY },
  });

  private buffer: IAnalyticsEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      void this.store.init().then(() => {
        // Seed from whatever's already persisted, merged ahead of anything
        // tracked (into `this.buffer`) before init() resolved, so an early
        // event doesn't get lost or reordered before real history.
        this.buffer = [...(this.store.read(ANALYTICS_IDB_KEY) ?? []), ...this.buffer].slice(-BUFFER_MAX);
      });
    }
  }

  track(event: string, props: Record<string, unknown> = {}): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const payload: IAnalyticsEvent = { event, ...props, ts: Date.now() };
    if (isDevMode()) console.log('[analytics]', payload);
    this.send(event, props);
    this.buffer = [...this.buffer, payload].slice(-BUFFER_MAX);
    this.scheduleFlush();
  }

  /** Forwards to GA4 / Meta only if consent has loaded their scripts (they set window.gtag / window.fbq). */
  private send(event: string, props: Record<string, unknown>): void {
    const w = window as unknown as ITrackingWindow;
    const params = toGa4Params(props);
    w.gtag?.('event', event, params);
    const metaEvent = META_PIXEL_EVENTS[event];
    if (metaEvent) w.fbq?.('track', metaEvent, params);
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.store.write(ANALYTICS_IDB_KEY, this.buffer);
    }, FLUSH_DELAY_MS);
  }
}
